import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import { WorkitemStatus, WorkItemStatusToLabel } from '../ProjectSelectColumn/types';

type LabelOverrides = Partial<Record<WorkitemStatus, string>>;

interface StatusTabsProps {
  statuses: WorkitemStatus[];
  selectedStatus: WorkitemStatus | null;
  onStatusChange: (status: WorkitemStatus | null) => void;
  selectedProject?: any;
  countIsLoading?: boolean;
  labelOverrides?: LabelOverrides;
}

const StatusTabs: React.FC<StatusTabsProps> = ({
  statuses,
  selectedStatus,
  onStatusChange,
  selectedProject,
  countIsLoading = false,
  labelOverrides = {},
}) => {
  const theme = useTheme();

  const getStatusCount = (status: WorkitemStatus): number => {
    return selectedProject?.workItemCounts?.[status] ?? 0;
  };

  const renderCount = (count: number) => {
    if (countIsLoading) {
      return '(...)';
    }
    return `(${count})`;
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    // Convert string value back to WorkitemStatus or null
    const status = newValue === 'null' ? null : (newValue as WorkitemStatus);
    onStatusChange(status);
  };

  // Convert selectedStatus to string for TabContext value
  const selectedValue = selectedStatus ?? 'null';

  return (
    <Box sx={{ marginBottom: '20px' }}>
      <TabContext value={selectedValue}>
        <Box
          sx={{
            display: 'flex',
            borderBottom: '1px solid lightgray',
          }}
        >
          <TabList
            onChange={handleChange}
            sx={{
              '& .Mui-selected': {
                color: theme.palette.artisticTextInteractive.main,
              },
            }}
            variant="scrollable"
          >
            {statuses.map((status) => {
              const label =
                (labelOverrides[status] as string) || WorkItemStatusToLabel[status] || status;
              const count = getStatusCount(status);
              return (
                <Tab
                  key={status}
                  data-testid={`status-tab-${status}`}
                  style={{ minWidth: '76px', padding: '6px 32px' }}
                  label={
                    <Typography variant="body1">
                      {label} {renderCount(count)}
                    </Typography>
                  }
                  value={status}
                />
              );
            })}
          </TabList>
        </Box>
      </TabContext>
    </Box>
  );
};

export default StatusTabs;

